import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { Button, Flex, Group, Notification, Paper, ScrollArea, Select, Stack, Table, Title } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { AppMedError } from '../../../global/error/AppMedError.ts';
import {
    ITestKitOrderLine,
    ITestKitOrderPayload,
    TestKitOrderPayload,
} from '../../../global/entities/TestKitOrder/TestKitOrder.ts';
import { usePostTestKitOrder } from '../../../global/entities/TestKitOrder/mutations/usePostTestKitOrderMutations.tsx';
import { usePuTestKitOrder } from '../../../global/entities/TestKitOrder/mutations/usePutTestKitOrderMutations.tsx';
import { useGetTestKitOrderById } from '../../../global/entities/TestKitOrder/queries/useGetTestKitOrderById.tsx';
import { useGetTestKitOrders } from '../../../global/entities/TestKitOrder/queries/useGetTestKitOrders.tsx';
import { IconArrowLeft, IconHospital, IconTestPipe } from '@tabler/icons-react';
import { useDisclosure, useElementSize } from '@mantine/hooks';
import TestKitModal from '../Modal/TestKitModal.tsx';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import TestKitFormRow from './TestKitFormRow.tsx';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import { useGetTestKitOrderNew } from '../../../global/entities/TestKitOrder/queries/useGetTestKitOrderNew.tsx';
import { Placeholder } from '../../../global/component/molecules/Placeholder.tsx';
import { theme } from '../../../global/style/theme.tsx';

const initValues: TestKitOrderPayload = {
    healthcareFacilityID: 0,
    orderStatusID: 1,
    testKitOrderLines: [],
};

const TestKitForm: React.FC = inject()(
    observer(() => {
        const { t } = useTranslation();
        const params = useParams<{ id: string; tabId: string }>();
        const [error, setError] = useState<string | null>(null);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const postTestKitOrder = usePostTestKitOrder();
        const putTestKitOrder = usePuTestKitOrder();
        const [opened, { open, close }] = useDisclosure(false);
        const {
            data: testKitOrder,
            refetch: refetchTTestKitOrder,
            isLoading: isLoadingTestKitOrder,
        } = useGetTestKitOrderById();
        const { data: testKitOrderNew } = useGetTestKitOrderNew();
        const { refetch: refetchTestKitOrders } = useGetTestKitOrders();
        const navigate = useNavigate();
        const { ref } = useElementSize();
        const [errors, setErrors] = useState<Record<number, Partial<Record<keyof ITestKitOrderLine, string>>>>({});
        const [rowsError, setRowsError] = useState(false);
        const { setSorting, sortBy, reverseSortDirection, setSortedData, sortedData } = useTable();

        const addNewRow = (newRow: ITestKitOrderLine) => {
            setSortedData((prevRows) => {
                const usedIds = new Set(prevRows.map((row) => row.id));
                const getLowestAvailableId = () => {
                    let id = 1;
                    while (usedIds.has(id)) 
                        id++;
                    
                    return id;
                };
                const newRows = Array.from({ length: newRow.qty ?? 1 }, () => {
                    const id = getLowestAvailableId();
                    usedIds.add(id);
                    return { ...newRow, id };
                });

                return [...prevRows, ...newRows];
            });
        };

        const schema = z.object({
            healthcareFacilityID: z.number().min(1, { message: t('formError.facilityRequired') }),
        });

        const form = useForm<TestKitOrderPayload>({
            mode: 'controlled',
            initialValues: initValues,
            validate: zodResolver(schema),
        });

        const handleCancel = () => {
            form.reset();
            navigate('/adminDashboard/testKit');
        };

        const onClickDelete = (id: number) => {
            setSortedData((prevRows) => prevRows.filter((row) => row.id !== id));
        };

        const rowsIsValid = () => {
            setRowsError(sortedData.length === 0);
            if (sortedData.length === 0) 
                return false;

            let isValid = true;
            const newErrors: Record<number, Partial<Record<keyof ITestKitOrderLine, string>>> = {};

            sortedData.forEach((row) => {
                const rowErrors: Partial<Record<keyof ITestKitOrderLine, string>> = {};

                Object.entries(row).forEach(([key, value]) => {
                    const field = key as keyof ITestKitOrderLine;

                    if (!value || (typeof value === 'string' && value.trim() === '')) {
                        rowErrors[field] = t(`formError.${field}Required`);
                        isValid = false;
                    }
                });

                if (Object.keys(rowErrors).length > 0) 
                    newErrors[row.id!] = rowErrors;
                
            });

            if (Object.keys(newErrors).length > 0) 
                setErrors(newErrors);

            return isValid;
        };

        useEffect(() => {
            setSortedData([]);
            if (testKitOrder?.testKitOrderLines?.length) 
                setSortedData(testKitOrder?.testKitOrderLines);
            
        }, [setSortedData, testKitOrder?.testKitOrderLines]);

        const handleSubmit = async (formValues: ITestKitOrderPayload) => {
            if (!rowsIsValid()) 
                return;
            
            setError(null);
            setIsSubmitting(true);
            try {
                await (params.id !== '0' ? putTestKitOrder : postTestKitOrder).mutateAsync({
                    healthcareFacilityID: formValues.healthcareFacilityID,
                    orderStatusID: formValues.orderStatusID,
                    testKitOrderLines: sortedData.map((row: ITestKitOrderLine) => ({
                        id: row.id!,
                        testKitTypeID: row.testKitTypeID,
                        sampleNumber: row.sampleNumber!,
                        trackingNumber: row.trackingNumber!,
                        batchNumber: row.batchNumber,
                        expirationDate: row.expirationDate!,
                        price: row.price || 0,
                    })),
                });
            } catch (e) {
                if (e instanceof AppMedError) 
                    setError(e.message);
                 else 
                    setError('An unexpected error occurred.');
                
            } finally {
                setIsSubmitting(false);
                refetchTestKitOrders().then(() => {
                    if (params.id !== '0') 
                        refetchTTestKitOrder();
                    
                    handleCancel();
                });
            }
        };

        useEffect(() => {
            form.reset();
            setErrors({});
            setSortedData([]);
            if (testKitOrder && params.id && testKitOrder.id !== form.getValues().id) {
                setSortedData(
                    testKitOrder.testKitOrderLines.map((line: ITestKitOrderLine) => ({
                        ...line,
                        expirationDate: new Date(line.expirationDate),
                        testKitTypeID: parseInt(line.testKitTypeID.toString()),
                    })),
                );
                form.setValues({
                    healthcareFacilityID: testKitOrder.healthcareFacilityID,
                    orderStatusID: testKitOrder.orderStatusID,
                });
                form.clearErrors();
            }
        }, [testKitOrder, params.id]);

        if (isLoadingTestKitOrder) 
            return <Loading visible={true} loadingMessage={'Loading...'} />;

        return (
            <>
                <Flex mb={'lg'} mt={'lg'} align={'center'} justify={'space-between'}>
                    <Button
                        leftSection={<IconArrowLeft fontWeight={700} color={theme.colors?.blue?.toString()} />}
                        onClick={() => navigate('/adminDashboard/testKit')}
                        variant="transparent"
                    >
                        <Title c="black" order={4}>
                            {testKitOrder ? `${t('testKit.title')} ${testKitOrder.id.toString()}` : t('testKit.add')}
                        </Title>
                    </Button>
                </Flex>
                <Paper w="100%" ref={ref}>
                    <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
                        <Stack gap={'md'}>
                            <Group grow justify={'space-between'}>
                                <Select
                                    maw={250}
                                    label={t('hcf.title')}
                                    placeholder={t('hcf.title')}
                                    leftSection={<IconHospital size={16} />}
                                    data={testKitOrderNew?.healthcareFacilitiesCombobox}
                                    onChange={(value) => form.setFieldValue('healthcareFacilityID', Number(value))}
                                    value={
                                        form.values.healthcareFacilityID > 0
                                            ? form.values.healthcareFacilityID.toString()
                                            : null
                                    }
                                    error={form.getInputProps('healthcareFacilityID').error}
                                />
                                <Select
                                    maw={150}
                                    label={t('global.status')}
                                    data={testKitOrderNew?.orderStatusCombobox}
                                    onChange={(value) => form.setFieldValue('orderStatusID', Number(value))}
                                    value={form.values.orderStatusID > 0 ? form.values.orderStatusID.toString() : null}
                                    allowDeselect={false}
                                />
                            </Group>
                            <ScrollArea w="100%" pb={'md'}>
                                <Table highlightOnHover mih={50}>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Th
                                                width={200}
                                                sorted={sortBy === 'testKitTypeID'}
                                                reversed={reverseSortDirection}
                                                onSort={() => setSorting('testKitTypeID', sortedData)}
                                            >
                                                {t('modal.testKitModal.testKit')}
                                            </Th>
                                            <Th
                                                sorted={sortBy === 'sampleNumber'}
                                                reversed={reverseSortDirection}
                                                onSort={() => setSorting('sampleNumber', sortedData)}
                                            >
                                                {t('modal.testKitModal.sampleNumber')}
                                            </Th>
                                            <Th
                                                sorted={sortBy === 'batchNumber'}
                                                reversed={reverseSortDirection}
                                                onSort={() => setSorting('batchNumber', sortedData)}
                                            >
                                                {t('modal.testKitModal.batchNumber')}
                                            </Th>
                                            <Th
                                                sorted={sortBy === 'trackingNumber'}
                                                reversed={reverseSortDirection}
                                                onSort={() => setSorting('trackingNumber', sortedData)}
                                            >
                                                {t('modal.testKitModal.trackingNumber')}
                                            </Th>

                                            <Th
                                                sorted={sortBy === 'expirationDate'}
                                                reversed={reverseSortDirection}
                                                onSort={() => setSorting('expirationDate', sortedData)}
                                            >
                                                {t('modal.testKitModal.expirationDate')}
                                            </Th>
                                            <Th
                                                width={120}
                                                sorted={sortBy === 'price'}
                                                reversed={reverseSortDirection}
                                                onSort={() => setSorting('price', sortedData)}
                                            >
                                                {t('modal.testKitModal.price')}
                                            </Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {sortedData.map((row: ITestKitOrderLine, index) => (
                                            <TestKitFormRow
                                                row={row}
                                                key={index}
                                                onClickDelete={onClickDelete}
                                                setRows={setSortedData}
                                                errors={errors[row.id!] || {}}
                                            />
                                        ))}
                                    </Table.Tbody>
                                </Table>
                                {!sortedData.length && rowsError && errors && (
                                    <Placeholder color={'red'} icon={<IconTestPipe />} title={t('testKit.please')} />
                                )}
                            </ScrollArea>
                            <Flex justify={'flex-end'} w={'100%'}>
                                <Button ml={'auto'} variant="default" onClick={open}>
                                    {t('testKit.new')}
                                </Button>
                            </Flex>
                            <Flex justify={'flex-end'} gap={'md'} w={'100%'}>
                                <Button ml={'auto'} variant="default" onClick={handleCancel}>
                                    {t('button.cancel')}
                                </Button>
                                <Button loading={isSubmitting} type="submit">
                                    {t('button.save')}
                                </Button>
                            </Flex>
                        </Stack>
                    </form>
                    <TestKitModal onAddRow={addNewRow} opened={opened} closeModal={close} />
                    {error && (
                        <Notification color="red" onClose={() => setError(null)}>
                            {error}
                        </Notification>
                    )}
                </Paper>
            </>
        );
    }),
);

const WrappedTestKitFormTable: React.FC = () => {
    return (
        <TableContextProvider>
            <TestKitForm />
        </TableContextProvider>
    );
};

export default WrappedTestKitFormTable;
